import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Typography, Chip, Stack, Tooltip } from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useTranslation } from "react-i18next";

import supabase from "../../../utils/supabase";
import fetchSupabaseAll from "../../../utils/fetchSupabaseAll";
import { extractAuthorNames, shouldBracketAuthorship } from "../../../utils/scientificNameFormatter";
import DialogDisplayScientificNameDetails from "./DialogDisplayScientificNameDetails/DialogDisplayScientificNameDetails";

export default function ScientificNameDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation(["taxonomy", "home", "common"]);

  const passedInitial = location.state?.initialScientificName;
  const prefetchKey = useMemo(() => (id ? `scientificName_prefetch_${id}` : null), [id]);

  const [initialScientificName, setInitialScientificName] = useState(passedInitial || null);
  const [allScientificNames, setAllScientificNames] = useState([]);
  const [allAuthorsData, setAllAuthorsData] = useState([]);
  const [allPublications, setAllPublications] = useState({ data: [] });
  const [descendantCountMap, setDescendantCountMap] = useState({});
  const [latestAudit, setLatestAudit] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const allScientificNamesMap = useMemo(
    () => new Map(allScientificNames.map((sn) => [sn.id, sn])),
    [allScientificNames],
  );

  // (1) Prefetch from sessionStorage
  useEffect(() => {
    if (!prefetchKey) return;
    const cached = sessionStorage.getItem(prefetchKey);
    if (!cached) return;

    let parsed = null;
    try {
      parsed = JSON.parse(cached);
    } catch (e) {
      console.warn("[ScientificNameDetailsPage] prefetch parse failed", e);
    }
    sessionStorage.removeItem(prefetchKey);

    if (!parsed) return;
    if (parsed.initialScientificName) {
      setInitialScientificName(parsed.initialScientificName);
    }
    if (Array.isArray(parsed.allScientificNames) && parsed.allScientificNames.length) {
      setAllScientificNames(parsed.allScientificNames);
    }
    if (Array.isArray(parsed.scientificNameAuthors) && parsed.scientificNameAuthors.length) {
      setAllAuthorsData(parsed.scientificNameAuthors);
    }
    if (parsed.allPublications?.data) {
      setAllPublications(parsed.allPublications);
    }
    if (parsed.descendantCountMap) {
      setDescendantCountMap(parsed.descendantCountMap);
    }
  }, [prefetchKey]);

  // (2) Fetch missing data from Supabase
  useEffect(() => {
    // プリフェッチされたデータがある場合は、再取得しない
    if (initialScientificName && allScientificNames.length > 0 && allAuthorsData.length > 0) {
      return;
    }

    const needsNames = allScientificNames.length === 0;
    const needsAuthors = allAuthorsData.length === 0;
    // 文献情報は、このページでは一旦自動取得しない（ダイアログの挙動に合わせる）
    if (!needsNames && !needsAuthors) return;

    const fetchLists = async () => {
      setLoading(true);
      try {
        const [namesRes, authorsRes] = await Promise.all([
          needsNames ? fetchSupabaseAll("scientific_names", `*`) : Promise.resolve(null),
          needsAuthors
            ? supabase.from("scientific_name_and_author").select(`*, researcher:researcher_id (id, first_name, last_name)`)
            : Promise.resolve(null),
        ]);

        if (namesRes) {
          setAllScientificNames(namesRes);
        }
        if (authorsRes) {
          if (authorsRes.error) throw authorsRes.error;
          const authorsWithIds = (authorsRes.data || []).map((rel) => ({
            ...rel,
            id: `${rel.scientific_name_id}-${rel.author_order ?? 0}-${rel.researcher?.id ?? ""}`,
          }));
          setAllAuthorsData(authorsWithIds);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLists();
  }, [initialScientificName, allScientificNames.length, allAuthorsData.length]);

  // (3) Set initialScientificName from fetched list if not passed
  useEffect(() => {
    if (initialScientificName) return;
    const found = allScientificNames.find((sn) => sn.id === id);
    setInitialScientificName(found || null);
  }, [allScientificNames, id, initialScientificName]);

  const [actorNameMap, setActorNameMap] = useState(new Map());
  const resolveActorName = useCallback((log) => {
    if (log?.actor_id && actorNameMap.has(log.actor_id)) {
      const disp = actorNameMap.get(log.actor_id);
      if (disp && typeof disp === "object") {
        const lang = (i18n.language || "").split("-")[0];
        return disp[lang] || disp.en || Object.values(disp)[0];
      }
      return disp;
    }
    return t("taxonomy:details.unknownUser");
  }, [actorNameMap, i18n.language, t]);

  const mergeMemberNames = useCallback((members) => {
    if (!members) return;
    setActorNameMap((prev) => {
      const map = new Map(prev);
      (members || []).forEach((m) => {
        if (m.actor_id) map.set(m.actor_id, m.display_name);
        if (m.auth_user_id) map.set(m.auth_user_id, m.display_name);
        map.set(m.id, m.display_name);
      });
      return map;
    });
  }, []);

  const fetchMembersWithFallback = useCallback(async (ids = null) => {
    const hasIds = Array.isArray(ids) && ids.length > 0;
    const quoted = hasIds ? ids.map((id) => `"${id}"`).join(",") : "";
    const selectColsWithActor = "id, auth_user_id, actor_id, display_name";
    const selectColsNoActor = "id, auth_user_id, display_name";
    const buildQuery = (includeActor) => {
      let q = supabase.from("admin_team_members").select(includeActor ? selectColsWithActor : selectColsNoActor);
      if (hasIds) {
        const orClause = includeActor
          ? `id.in.(${quoted}),auth_user_id.in.(${quoted}),actor_id.in.(${quoted})`
          : `id.in.(${quoted}),auth_user_id.in.(${quoted})`;
        q = q.or(orClause);
      }
      return q;
    };
    const { data, error } = await buildQuery(true);
    if (!error) return data;
    if (error.code !== "42703") throw error;
    const retry = await buildQuery(false);
    if (retry.error) throw retry.error;
    console.warn("admin_team_members.actor_id not found; falling back to auth_user_id/id");
    return retry.data;
  }, []);

  // (4) Fetch latest audit log for the header
  useEffect(() => {
    let cancelled = false;
    const fetchAudit = async () => {
      if (!id) {
        setLatestAudit(null);
        return;
      }
      const res = await supabase
        .from("audit_log")
        .select("row_id, actor_id, created_at")
        .eq("table_name", "scientific_names")
        .eq("row_id", id)
        .order("created_at", { ascending: false })
        .limit(1);
      if (res.error) {
        console.warn("[audit_log] fetch failed:", res.error.message);
        return;
      }
      if (!cancelled) {
        const log = res.data?.[0] || null;
        setLatestAudit(log);
        const actorIds = log?.actor_id ? [log.actor_id] : [];
        if (actorIds.length) {
          try {
            const members = await fetchMembersWithFallback(actorIds);
            mergeMemberNames(members);
          } catch (err) {
            console.error("failed to fetch actor names", err);
          }
        }
      }
    };
    fetchAudit();
    return () => {
      cancelled = true;
    };
  }, [id, fetchMembersWithFallback, mergeMemberNames]);

  // --- Header Formatting Logic (from Taxonomy.jsx) ---

  const isBotActor = useCallback((log) => {
    const actor = String(log?.actor_display || "").toLowerCase();
    return actor.includes("bot");
  }, []);

  const botByAudit = useMemo(() => isBotActor(latestAudit), [latestAudit, isBotActor]);

  const latestAuditText = useMemo(() => {
    if (!latestAudit) return null;
    const when = latestAudit.changed_at || latestAudit.created_at;
    const who = resolveActorName(latestAudit);
    if (!when) return `Updated by ${who}`;
    const d = new Date(when);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return `Updated ${dateStr} by ${who}`;
  }, [latestAudit]);

  const formatAuthorsInline = useCallback((names = []) => {
    if (!names.length) return "";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
  }, []);

  const titleAuthors = useMemo(() => {
    if (!initialScientificName?.id) return "";
    const rels = allAuthorsData
      .filter((r) => r.scientific_name_id === initialScientificName.id)
      .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0));
    const names = extractAuthorNames({
      _authority_authors: rels.map((r) => r.researcher?.last_name_eng || r.researcher?.last_name || r.researcher?.first_name || ""),
      authors_arr: rels.map((r) => r.researcher?.last_name_eng || r.researcher?.last_name || r.researcher?.first_name || ""),
    });
    return formatAuthorsInline(names);
  }, [initialScientificName?.id, allAuthorsData, formatAuthorsInline]);

  const formatTitleScientificName = useCallback(() => {
    if (!initialScientificName) return "Scientific Name";
    const sn = initialScientificName;
    const rels = allAuthorsData
      .filter((r) => r.scientific_name_id === sn.id)
      .sort((a, b) => (a.author_order ?? 0) - (b.author_order ?? 0));
    const namesFromRels = rels.map((r) => r.researcher?.last_name_eng || r.researcher?.last_name || r.researcher?.first_name || "").filter(Boolean);
    const names = namesFromRels.length ? namesFromRels : extractAuthorNames(sn);
    const authorsText = formatAuthorsInline(names);
    const year = sn.authority_year || "";
    const gbifAuth = sn.authority_from_gbif || "";
    let authority;
    if (authorsText) {
      authority = authorsText && year ? `${authorsText}, ${year}` : authorsText || year || "";
    } else if (gbifAuth) {
      authority = gbifAuth;
    } else {
      authority = year;
    }

    const ancestors = [];
    let cur = sn;
    const seen = new Set();
    while (cur?.current_parent && !seen.has(cur.current_parent)) {
      seen.add(cur.current_parent);
      const parent = allScientificNamesMap.get(cur.current_parent);
      if (!parent) break;
      ancestors.unshift(parent);
      cur = parent;
    }

    const italics = (n) => (n ? `<i>${n}</i>` : "");
    const rankNorm = (sn.current_rank || "").trim().toLowerCase();
    const validName = sn.name_spell_valid || "";
    const originalRaw = (sn.name_spell_original || "").split("|")[0].trim();
    const originalNoAuth = originalRaw.replace(/\([^)]*\)/g, " ").trim();
    const tokens = originalNoAuth.split(/\s+/).filter(Boolean);
    const tokenGenus = tokens[0] || "";
    const tokenSpecies = tokens[1] || "";
    const tokenSubspecies = tokens[2] || "";
    let nameStr = "";

    if (["genus", "species", "subspecies"].includes(rankNorm)) {
      const epithet = italics(validName);
      if (rankNorm === "species" || rankNorm === "subspecies") {
        const genus = ancestors.find((a) => (a.current_rank || "").toLowerCase() === "genus");
        const subgenus = ancestors.find((a) => (a.current_rank || "").toLowerCase() === "subgenus");
        const genusName = italics(genus?.name_spell_valid || tokenGenus || sn._parent_label || "(unknown genus)");
        const subgenusPart = subgenus ? `(${italics(subgenus.name_spell_valid)}) ` : "";

        if (rankNorm === "species") {
          const speciesEpithet = epithet || italics(tokenSpecies);
          nameStr = `${genusName} ${subgenusPart}${speciesEpithet}`.trim();
        } else {
          const speciesAncestor = ancestors.find((a) => (a.current_rank || "").toLowerCase() === "species");
          const speciesEpithet = italics(speciesAncestor?.name_spell_valid || tokenSpecies || "");
          const subspEpithet = epithet || italics(tokenSubspecies);
          nameStr = `${genusName} ${subgenusPart}${speciesEpithet} ${subspEpithet}`.trim();
        }
      } else {
        nameStr = epithet;
      }
    } else if (rankNorm === "subgenus") {
      const genus = ancestors.find((a) => (a.current_rank || "").toLowerCase() === "genus");
      nameStr = `${italics(genus?.name_spell_valid || tokenGenus || "(unknown genus)")} (${italics(validName)})`;
    } else {
      nameStr = validName;
    }

    if (authority && shouldBracketAuthorship(sn, allScientificNamesMap)) {
      authority = `(${authority})`;
    }

    return `${nameStr}${authority ? ` ${authority}` : ""}`;
  }, [initialScientificName, allAuthorsData, allScientificNamesMap, formatAuthorsInline]);

  // --- Render ---

  if (loading && !initialScientificName) return <div>Loading...</div>;
  if (error && !initialScientificName) return <div>Error: {error}</div>;
  if (!initialScientificName) return <div>No data found.</div>;

  return (
    <Box>
      <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider" }}>
        <Typography
          variant="h4"
          component="div"
          dangerouslySetInnerHTML={{ __html: formatTitleScientificName() }}
          sx={{ lineHeight: 1.3, fontWeight: 700, wordBreak: "break-word" }}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
          {initialScientificName?.current_rank && (
            <Chip
              size="medium"
              icon={<CategoryIcon fontSize="small" />}
              label={`Rank: ${initialScientificName.current_rank}`}
              color="default"
              sx={{
                height: 26,
                fontWeight: 700,
                "& .MuiChip-label": { px: 1, fontSize: "0.8rem", fontWeight: 700, letterSpacing: 0.3 },
                "& .MuiChip-icon": { fontSize: "1.1rem" },
                bgcolor: "rgba(127,255,212,0.08)",
                border: "1px solid rgba(127,255,212,0.4)",
                color: "text.secondary",
              }}
            />
          )}
          {(botByAudit || (!titleAuthors && initialScientificName?.authority_from_gbif)) && (
            <Tooltip title="Automatically added via GBIF data. Please verify this information before relying on it.">
              <Chip
                size="medium"
                icon={<SmartToyIcon fontSize="small" />}
                label="BOT"
                color="default"
                sx={{
                  height: 26,
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 0.9, fontSize: "0.8rem", fontWeight: 700, letterSpacing: 0.3 },
                  "& .MuiChip-icon": { fontSize: "1.1rem" },
                }}
              />
            </Tooltip>
          )}
          {latestAuditText && (
            <Chip
              size="medium"
              label={latestAuditText}
              color="default"
              sx={{
                height: 26,
                fontWeight: 700,
                "& .MuiChip-label": { px: 0.9, fontSize: "0.78rem", fontWeight: 700, letterSpacing: 0.2 },
                bgcolor: "rgba(127,255,212,0.08)",
                border: "1px solid rgba(127,255,212,0.3)",
                color: "text.secondary",
              }}
            />
          )}
        </Stack>
      </Box>

      <DialogDisplayScientificNameDetails
        initialScientificName={initialScientificName}
        allScientificNames={allScientificNames}
        scientificNameAuthors={allAuthorsData}
        allTaxonomicActs={[]}
        allReferences={allPublications}
        descendantCountMap={descendantCountMap}
        isPage={true}
      />
    </Box>
  );
}
